import { ModelTypeOptions, OutcomeMeasureTypeLabels } from "../../../models/simulator/options";
import {
	IModelInput,
	IModelSectionInput,
	IModelValidity,
	INestedModelInput,
	INestedModelSectionInput,
	IPatientPopulationInput,
	IScenarioInput,
	ISimulatorInput,
	ITreatmentDurationInput,
	ITreatmentRegimenInput,
} from "./inputs";
import { ContextService } from "@lyfegen-platform/utils";
import { getEnrollmentInput, getMonthNumber } from "./utils";
import { modelPriorityList } from "../details/utils/model-priority";
import { IndicationPatientData } from "./patient-service/patient-service-input.model";

export interface IContextArgs {
	sectionId: string;
	context: SimulationContext;
}

export interface IAdditionalDiscount {
	originalDiscount?: number;
	patientShare?: number;
	patientSplitId: string;
	modelType: ModelTypeOptions;
	discount: number;
	packName?: string;
	discountName: string;
	conditionType?: string;
	discountCount?: number;
	modelValidity?: IModelValidity;
	id: string;
}

export interface IAdditionalDiscountNotIncluded {
	patientSplitId: string;
	month: number;
	count: number;
	packName: string;
	discountName: string;
	scenarioId: string;
}

export type IAddDiscountNotIncludedArgs = IAdditionalDiscountNotIncluded & { sectionId: string };

export interface IAggregationValue {
	patientSplitId: string;
	value: number;
}

export interface IFinancialModelInput extends INestedModelSectionInput {
	type: ModelTypeOptions;
	validity?: IModelValidity;
}

export interface ISectionContext {
	additionalDiscounts: Map<number, Array<IAdditionalDiscount>>;
	discountNotIncluded: Map<number, Array<IAdditionalDiscountNotIncluded>>;
	volumeBasedAggregationValues: Array<IAggregationValue>;
	freeOrDiscountedAggregationValues: Array<IAggregationValue>;
	lastAggregationResetMonth: Array<IAggregationValue>;
}

interface IAddDiscountArgs {
	sectionId: string;
	patientSplitId: string;
	modelType: ModelTypeOptions;
	packName: string;
	discount: number;
	month: number;
	discountName: string;
	originalDiscount?: number;
	conditionType?: string;
	discountCount?: number;
	modelValidity?: IModelValidity;
	patientShare?: number;
	id: string;
}

export class SimulationContext {
	private input: ISimulatorInput;
	private sectionInputs: Map<string, IModelSectionInput>;
	private context: Map<string, ISectionContext>;
	private cache: ContextService;
	private volumeBasedTotalAggregationValue: number;
	private freeOrDiscountedTotalAggregationValue: number;

	constructor(simulationInput: ISimulatorInput) {
		this.input = simulationInput;
		this.sectionInputs = new Map(simulationInput.model.sections.map((section) => [section.id, section]));
		this.context = new Map();
		this.cache = new ContextService();
		this.volumeBasedTotalAggregationValue = 0;
		this.freeOrDiscountedTotalAggregationValue = 0;
	}

	getIsNested(scenario: IScenarioInput) {
		let isNested = true;
		this.input.models.forEach((model) => {
			model.sections.forEach((section) => {
				section.scenarios.forEach((parentScenario) => {
					if (parentScenario === scenario || parentScenario.id === scenario.id) {
						isNested = false;
					}
				});
			});
		});

		return isNested;
	}

	getSimulationStartDate() {
		return this.input.simulationDuration.startDate;
	}

	getSimulationEndDate() {
		return this.input.simulationDuration.endDate;
	}

	getVolumeBasedTotalAggregationValue() {
		return this.volumeBasedTotalAggregationValue;
	}

	getFreeOrDiscountedTotalAggregationValue() {
		return this.freeOrDiscountedTotalAggregationValue;
	}

	addVolumeBasedTotalAggregationValue(val: number) {
		this.volumeBasedTotalAggregationValue += val;
	}

	addFreeOrDiscountedTotalAggregationValue(val: number) {
		this.freeOrDiscountedTotalAggregationValue += val;
	}

	resetVolumeBasedTotalAggregationValue(sectionId: string) {
		this.volumeBasedTotalAggregationValue = 0;
		const section = this.getSection(sectionId);
		if (!section) throw Error("Invalid section");
		section.volumeBasedAggregationValues = [];
		this.context.set(sectionId, section);
	}

	private getIndication(indicationId: string) {
		return this.input.indications.find((indication) => indication.id === indicationId);
	}

	getPatientServiceInput(sectionId: string) {
		const section = this.sectionInputs.get(sectionId);

		if (!section || !section.indications) throw Error("Invalid section");

		const indicationsPatientData: Array<IndicationPatientData> = [];

		section.indications.forEach((indicationId) => {
			const indication = this.getIndication(indicationId);
			if (indication) {
				indicationsPatientData.push({
					indicationId: indicationId,
					name: indication.name,
					index: indication.index,
					treatmentDuration: getEnrollmentInput(
						this.input.treatmentDuration,
						indication.treatmentDuration
					) as ITreatmentDurationInput,
					patientEnrollment: getEnrollmentInput(
						this.input.patientPopulation,
						indication.patientPopulation
					) as IPatientPopulationInput,
				});
			}
		});

		return indicationsPatientData;
	}

	getTreatmentServiceInput(sectionId: string, indicationId: string) {
		const section = this.sectionInputs.get(sectionId);

		if (!section) throw Error("Invalid section");
		if (section.indications.indexOf(indicationId) < 0) throw Error("Invalid indication on section");

		return {
			productPricing: this.input.productPricing,
			patientWeight: this.input.patientWeight,
			treatmentRegimen: getEnrollmentInput(
				this.input.treatmentRegimen,
				this.getIndication(indicationId)?.treatmentRegimen
			) as Array<ITreatmentRegimenInput>,
		};
	}

	getProductInput(sectionId: string) {
		const section = this.sectionInputs.get(sectionId);
		if (!section) throw Error("Invalid section");
		return {
			productPacks: this.input.productPacks,
		};
	}

	getFinancialServiceInput(sectionId: string) {
		const models = this.getSectionModels(sectionId);

		return {
			models,
			straightDiscount: this.input.straightDiscount,
			productPacks: this.input.productPacks,
		};
	}

	getEvaluationThresholds(sectionId: string) {
		const models = this.getSectionModels(sectionId);
		const model = models.find((model) => model.type === ModelTypeOptions.OUTCOME_BASED);

		return model?.outcomeMeasureType === OutcomeMeasureTypeLabels.OTHER ? model?.evaluationThresholds || [] : [];
	}

	private getSectionModels(sectionId: string) {
		const section = this.sectionInputs.get(sectionId);
		if (!section) throw Error("Invalid section");

		const findModelWithSection = (models: IModelInput[], sectionId: string): IModelInput | undefined => {
			return models.find((model) => model.sections.some((section) => section.id === sectionId));
		};

		const sectionModel = findModelWithSection(this.input.models, sectionId);
		if (!sectionModel) throw Error("Invalid section");

		return [
			{ ...section, type: sectionModel.type, name: sectionModel.name, validity: sectionModel.validity },
			...this.getNestedSections(section.nestedModel),
		]
			.filter((model) => !!model.type)
			.sort(
				(firstModel, secondModel) =>
					modelPriorityList.findIndex((model) => model === firstModel?.type) -
					modelPriorityList.findIndex((model) => model === secondModel?.type)
			) as Array<IFinancialModelInput>;
	}

	private getNestedSections(model: INestedModelInput | undefined): Array<IFinancialModelInput> {
		if (!model) return [];
		return [
			{ ...model.sections[0], type: model.type, name: model.name, validity: model.validity },
			...this.getNestedSections(model.sections[0].nestedModel),
		];
	}

	getSimulationContext() {
		return this.context;
	}

	getSimulationCache() {
		return this.cache;
	}

	getSection(sectionId: string) {
		const section = this.context.get(sectionId);

		return section
			? section
			: {
					additionalDiscounts: new Map<number, Array<IAdditionalDiscount>>(),
					discountNotIncluded: new Map<number, Array<IAdditionalDiscountNotIncluded>>(),
					volumeBasedAggregationValues: [] as Array<IAggregationValue>,
					freeOrDiscountedAggregationValues: [] as Array<IAggregationValue>,
					lastAggregationResetMonth: [] as Array<IAggregationValue>,
			  };
	}

	getAdditionalDiscount(section: ISectionContext, month: number) {
		const additionalDiscounts = section.additionalDiscounts.get(month);
		return additionalDiscounts ? additionalDiscounts : ([] as Array<IAdditionalDiscount>);
	}

	getSectionLastMonth(sectionId: string) {
		const section = this.sectionInputs.get(sectionId);
		if (!section) throw Error("Invalid section");
		const { startDate, endDate } = this.input.simulationDuration;
		const startDateMoment = new Date(startDate);
		const endDateMoment = new Date(endDate);
		const monthCount = getMonthNumber(endDateMoment, startDateMoment);
		return monthCount;
	}

	addDiscount(args: IAddDiscountArgs) {
		const {
			sectionId,
			patientSplitId,
			packName,
			discount,
			originalDiscount,
			patientShare,
			modelType,
			modelValidity,
			month,
			discountName,
			conditionType,
			discountCount,
			id,
		} = args;
		const section = this.getSection(sectionId);
		const additionalDiscounts = this.getAdditionalDiscount(section, month);

		const allFound = additionalDiscounts.filter(
			(additionalDiscount) => additionalDiscount.patientSplitId === patientSplitId && additionalDiscount.modelType === modelType
		);

		const found = allFound.find((additionalDiscount) => additionalDiscount.id === id);

		if (found) {
			found.discount = discount;
			found.originalDiscount = originalDiscount;
			found.patientShare = patientShare;
			found.discountCount = discountCount;
		} else {
			additionalDiscounts.push({
				id,
				patientSplitId,
				packName,
				discount,
				originalDiscount,
				patientShare,
				modelType,
				modelValidity,
				discountName,
				conditionType,
				discountCount,
			});
		}

		section.additionalDiscounts.set(month, additionalDiscounts);
		this.context.set(sectionId, section);
	}

	getDiscountNotIncludedCount(section: ISectionContext, month: number) {
		const discountsNotIncluded = section.discountNotIncluded.get(month);
		return discountsNotIncluded ? discountsNotIncluded : ([] as Array<IAdditionalDiscountNotIncluded>);
	}

	addDiscountNotIncludedCount(args: IAddDiscountNotIncludedArgs) {
		const { sectionId, patientSplitId, month, count, packName, scenarioId, discountName } = args;
		const section = this.getSection(sectionId);
		const discountsNotIncluded = this.getDiscountNotIncludedCount(section, month);
		discountsNotIncluded.push({ patientSplitId, packName, month, count, discountName, scenarioId });
		section.discountNotIncluded.set(month, discountsNotIncluded);
		this.context.set(sectionId, section);
	}

	removeDiscountsNotIncluded(sectionId: string, patientSplitId: string, month: number) {
		const section = this.getSection(sectionId);
		const discountsNotIncluded = this.getDiscountNotIncludedCount(section, month);
		const filteredDiscountsNotIncluded = discountsNotIncluded.filter((e) => e.patientSplitId !== patientSplitId);
		section.discountNotIncluded.set(month, filteredDiscountsNotIncluded);
		this.context.set(sectionId, section);
	}

	getTreatmentYearResetPerCohort(sectionId: string, patientSplitId: string) {
		const section = this.getSection(sectionId);
		return section.lastAggregationResetMonth.find((aggregationValue) => aggregationValue.patientSplitId === patientSplitId)?.value;
	}

	getVolumeBasedAggregationValueForCohort(sectionId: string, patientSplitId: string) {
		const section = this.getSection(sectionId);
		return section.volumeBasedAggregationValues.find((aggregationValue) => aggregationValue.patientSplitId === patientSplitId);
	}

	getFreeOrDiscountedAggregationValueForCohort(sectionId: string, patientSplitId: string) {
		const section = this.getSection(sectionId);
		return section.freeOrDiscountedAggregationValues.find((aggregationValue) => aggregationValue.patientSplitId === patientSplitId);
	}

	updateTreatmentYearResetPerCohort(sectionId: string, patientSplitId: string, value: number) {
		const section = this.getSection(sectionId);
		const aggregationResetValue = section.lastAggregationResetMonth.find(
			(aggregationValue) => aggregationValue.patientSplitId === patientSplitId
		);
		if (aggregationResetValue) {
			aggregationResetValue.value += value;
		} else {
			section.lastAggregationResetMonth.push({ patientSplitId, value });
		}
	}

	updateVolumeBasedAggregationValue(sectionId: string, patientSplitId: string, value: number) {
		const section = this.getSection(sectionId);
		const aggregationValue = section.volumeBasedAggregationValues.find(
			(aggregationValue) => aggregationValue.patientSplitId === patientSplitId
		);

		if (aggregationValue) {
			aggregationValue.value += value;
		} else {
			section.volumeBasedAggregationValues.push({ patientSplitId, value });
		}

		this.context.set(sectionId, section);
	}

	updateFreeOrDiscountedAggregationValue(sectionId: string, patientSplitId: string, value: number) {
		const section = this.getSection(sectionId);
		const aggregationValue = section.freeOrDiscountedAggregationValues.find(
			(aggregationValue) => aggregationValue.patientSplitId === patientSplitId
		);

		if (aggregationValue) {
			aggregationValue.value += value;
		} else {
			section.freeOrDiscountedAggregationValues.push({ patientSplitId, value });
		}

		this.context.set(sectionId, section);
	}

	removeVolumeBasedAggregationValue(sectionId: string, patientSplitId: string) {
		const section = this.getSection(sectionId);

		if (section.volumeBasedAggregationValues) {
			section.volumeBasedAggregationValues = section.volumeBasedAggregationValues.filter(
				(aggregationValue) => aggregationValue.patientSplitId !== patientSplitId
			);
		}

		this.context.set(sectionId, section);
	}

	removeFreeOrDiscountedAggregationValue(sectionId: string, patientSplitId: string) {
		const section = this.getSection(sectionId);

		if (section.freeOrDiscountedAggregationValues) {
			section.freeOrDiscountedAggregationValues = section.freeOrDiscountedAggregationValues.filter(
				(aggregationValue) => aggregationValue.patientSplitId !== patientSplitId
			);
		}

		this.context.set(sectionId, section);
	}
}
